import { Check, Hourglass, Timer } from 'lucide-react';
import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Avatar } from 'shared/components/ui/Avatar';
import { Badge } from 'shared/components/ui/Badge';
import { EASE_OUT, SPRING } from 'shared/components/motion/tokens';
import { formatBRL } from 'shared/utils/format';
import { cn } from 'shared/utils/cn';

const TOTAL = 83.2;
const SHARE = TOTAL / 4;

const PLAYERS = [
  { name: 'Rafael Nunes', method: 'Pix' },
  { name: 'Júlia Melo', method: 'Cartão' },
  { name: 'Ana Paula', method: 'Pix' },
  { name: 'Diego Costa', method: 'Pix' },
];

// Quanto tempo a cena espera antes de o último amigo "pagar".
const LAST_PAYMENT_DELAY_MS = 2200;

// Vitrine do produto no hero: uma reserva dividida entre 4 amigos que se resolve sozinha
// na frente do visitante — a última cota é paga, a barra completa e a reserva confirma.
// É decorativa (aria-hidden): o texto do hero já diz o mesmo para leitores de tela.
export function HeroProductPreview() {
  const [allPaid, setAllPaid] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => setAllPaid(true), LAST_PAYMENT_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, []);

  const paidCount = allPaid ? 4 : 3;

  return (
    <div aria-hidden="true" className="relative mx-auto w-full max-w-md select-none pb-12 pt-16">
      <motion.div
        initial={{ opacity: 0, y: 24, rotate: 0 }}
        animate={{ opacity: 1, y: 0, rotate: -1.5 }}
        transition={{ duration: 0.8, ease: EASE_OUT, delay: 0.3 }}
        className="relative rounded-3xl border border-border/50 bg-surface p-6 shadow-card-hover"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs font-medium text-ink-secondary">Arena Praia Norte · Areia 3</p>
            <p className="mt-1 font-heading text-lg font-bold text-ink">Qui, 12 · 18:00 às 19:00</p>
          </div>
          <AnimatePresence mode="wait" initial={false}>
            <motion.span
              key={allPaid ? 'ok' : 'pending'}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.25 }}
            >
              <Badge tone={allPaid ? 'success' : 'warning'}>{allPaid ? 'Confirmada' : 'Aguardando cotas'}</Badge>
            </motion.span>
          </AnimatePresence>
        </div>

        <div className="mt-5 flex items-end justify-between">
          <div>
            <p className="text-xs text-ink-secondary">Total da quadra</p>
            <p className="font-heading text-2xl font-extrabold text-ink">{formatBRL(TOTAL)}</p>
          </div>
          <p className="text-sm text-ink-secondary">
            <span className="font-semibold text-ink">{paidCount} de 4</span> cotas pagas
          </p>
        </div>

        <div className="mt-3 h-2.5 w-full overflow-hidden rounded-full bg-chip">
          <motion.div
            className="h-full rounded-full bg-gradient-to-r from-primary to-primary-dark"
            initial={{ width: '0%' }}
            animate={{ width: `${(paidCount / 4) * 100}%` }}
            transition={{ duration: 0.9, ease: EASE_OUT, delay: allPaid ? 0 : 0.6 }}
          />
        </div>

        <div className="mt-5 flex flex-col divide-y divide-border/60">
          {PLAYERS.map((player, index) => {
            const isPaid = index < paidCount;
            return (
              <div key={player.name} className="flex items-center justify-between py-2.5">
                <div className="flex items-center gap-3">
                  <Avatar name={player.name} size="sm" />
                  <span className="text-sm font-medium text-ink">{player.name}</span>
                </div>
                <span
                  className={cn(
                    'flex items-center gap-1 text-sm font-medium transition-colors duration-500',
                    isPaid ? 'text-success' : 'text-warning'
                  )}
                >
                  {isPaid ? (
                    <Check className="h-3.5 w-3.5" strokeWidth={2.5} aria-hidden="true" />
                  ) : (
                    <Hourglass className="h-3.5 w-3.5" aria-hidden="true" />
                  )}
                  {isPaid ? player.method : 'pendente'} · {formatBRL(SHARE)}
                </span>
              </div>
            );
          })}
        </div>
      </motion.div>

      {/* Selos flutuantes: o que o produto resolve, em uma linha cada. */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0, y: [0, -6, 0] }}
        transition={{
          opacity: { delay: 0.9 },
          x: { delay: 0.9, ...SPRING },
          y: { duration: 5, repeat: Infinity, ease: 'easeInOut' },
        }}
        className="absolute -left-8 bottom-0 flex items-center gap-2 rounded-2xl border border-border/60 bg-surface/95 px-4 py-2.5 text-sm shadow-card-hover backdrop-blur"
      >
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-accent-soft text-primary-dark">
          <Timer className="h-4 w-4" aria-hidden="true" />
        </span>
        <span>
          <span className="block font-semibold text-ink">Vaga segura</span>
          <span className="text-xs text-ink-secondary">até 19:20 enquanto pagam</span>
        </span>
      </motion.div>

      <AnimatePresence>
        {allPaid && (
          <motion.div
            initial={{ opacity: 0, y: 12, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={SPRING}
            className="absolute -right-6 top-0 flex items-center gap-2 rounded-2xl border border-success/30 bg-surface px-4 py-2.5 text-sm shadow-card-hover"
          >
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-success-soft font-bold text-success">
              <Check className="h-4 w-4" strokeWidth={2.5} aria-hidden="true" />
            </span>
            <span>
              <span className="block font-semibold text-ink">Pix confirmado</span>
              <span className="text-xs text-ink-secondary">Diego pagou {formatBRL(SHARE)}</span>
            </span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
